import { lookupService } from "services/lookup.service";
import { Language, Lookups } from "types/index";
import { Option } from "@/types/option";
import {
  filterLookupDropdownOptions,
  mapLookupsToOptions,
  type LookupOptionSource,
} from "utils/lookupOptions";

export const OTHER_INDUSTRY_LABEL_PATTERN = /^others?$/i;

type CompanyIndustrySource = {
  industryId?: number | string | null;
  IndustryId?: number | string | null;
  industryLookupId?: number | string | null;
  IndustryLookupId?: number | string | null;
};

/** Read persisted company industry id from API payloads (camelCase or PascalCase). */
export function readCompanyIndustryId(
  company: CompanyIndustrySource | null | undefined
): string {
  const raw =
    company?.industryId ??
    company?.IndustryId ??
    company?.industryLookupId ??
    company?.IndustryLookupId;
  if (raw === undefined || raw === null || raw === "") return "";
  return String(raw);
}

export function companyIndustryIncludeId(
  company: CompanyIndustrySource | null | undefined
): number | null {
  const id = readCompanyIndustryId(company);
  if (!id) return null;
  const parsed = Number(id);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function mapLookupIndustriesToOptions(lookupItems: unknown[]): Option[] {
  return mapLookupsToOptions((lookupItems || []) as never[]);
}

export function getOtherIndustryOptionId(industries: Option[]): string | undefined {
  const other = industries.find((t) =>
    OTHER_INDUSTRY_LABEL_PATTERN.test(String(t.label || "").trim())
  );
  return other ? String(other.value) : undefined;
}

export function isOtherIndustrySelected(
  industryId: string | number | undefined,
  industries: Option[]
): boolean {
  const otherId = getOtherIndustryOptionId(industries);
  if (!otherId || industryId === undefined || industryId === "") return false;
  return String(industryId) === otherId;
}

export async function fetchCompanyIndustryOptions(
  includeLookupId?: string | number | null
): Promise<Option[]> {
  const lookupItems = await lookupService.getByParentCode(
    Lookups.CompanyIndustries,
    Language.ENGLISH,
    includeLookupId
  );
  return mapLookupsToOptions((lookupItems || []) as LookupOptionSource[]);
}

export function companyIndustryDropdownOptions(
  industries: Option[],
  industryId?: string | number | null,
  includeSelectedDraft = false
): Option[] {
  return filterLookupDropdownOptions(industries, industryId, includeSelectedDraft);
}

export function marketingIndustryLabels(industries: Option[]): string[] {
  return industries
    .filter(
      (option) =>
        !OTHER_INDUSTRY_LABEL_PATTERN.test(String(option.label || "").trim())
    )
    .map((option) => String(option.label || "").trim())
    .filter(Boolean);
}

export async function createCustomCompanyIndustry(
  industryName: string
): Promise<{ industryId: number; industries: Option[] }> {
  const trimmed = industryName.trim();
  if (!trimmed) {
    throw new Error("Industry is required");
  }

  const industriesParent =
    (await lookupService.getByCode(Lookups.CompanyIndustries)) ??
    (await lookupService.getByCode("COMPANY_INDUSTRIES"));

  if (!industriesParent) {
    throw new Error("Company industries parent lookup not found");
  }

  const parentId =
    industriesParent.id ?? (industriesParent as { Id?: number }).Id;
  const code = trimmed.replace(/\s+/g, "_").toUpperCase();

  const lookupDetails = [
    { id: 0, name: trimmed, languageId: Language.ENGLISH },
    { id: 0, name: trimmed, languageId: Language.ARABIC },
  ];

  const created = await lookupService.createDraft({
    code,
    valueEn: trimmed,
    valueAr: trimmed,
    parentId,
    isDraft: true,
    lookupDetails,
  } as never);

  const createdRaw = created as unknown as {
    id?: number | string;
    Id?: number | string;
  };
  const newId = Number(createdRaw?.id ?? createdRaw?.Id ?? 0);
  if (!newId) {
    throw new Error("Failed to create industry");
  }

  const industries = await fetchCompanyIndustryOptions(newId);

  return {
    industryId: newId,
    industries,
  };
}

export async function resolveIndustryIdForSave(
  industryId: string,
  industries: Option[],
  customIndustryName?: string
): Promise<{ industryId?: number }> {
  if (!industryId?.trim()) {
    return { industryId: undefined };
  }

  if (isOtherIndustrySelected(industryId, industries)) {
    const trimmed = customIndustryName?.trim();
    if (!trimmed) {
      throw new Error("Please enter your industry.");
    }
    const created = await createCustomCompanyIndustry(trimmed);
    return { industryId: created.industryId };
  }

  return { industryId: Number(industryId) };
}
